import { applyToJob } from '../services/job-applies';
import { CandidateValidationService } from '../services/candidate-validation';

export interface JobApplyResult {
    success: boolean;
    error?: string;
    missingFields?: string[];
    isValidationError?: boolean;
}

/**
 * Utilitário para candidatura a vagas com verificação de validação do perfil
 */
export class JobApplyHelper {
    /**
     * Verifica se o perfil está válido antes de permitir candidatura
     * @param candidateId ID do candidato (opcional, usa o ID do localStorage se não fornecido)
     * @returns Promise com resultado da verificação
     */
    static async checkProfileBeforeApply(candidateId?: string): Promise<{
        isValid: boolean;
        missingFields: string[];
        message: string;
    }> {
        try {
            const id = candidateId || localStorage.getItem('candidate_id');
            
            if (!id) {
                throw new Error('ID do candidato não encontrado');
            }

            const validation = await CandidateValidationService.validateProfile(id);
            return {
                isValid: validation.is_valid,
                missingFields: validation.missing_fields || [],
                message: this.extractMessage(validation.message)
            };
        } catch (error: any) {
            return {
                isValid: false,
                missingFields: [],
                message: error.message || 'Erro ao verificar perfil'
            };
        }
    }

    /**
     * Tenta se candidatar a uma vaga com verificação prévia de validação
     * @param jobpostId ID da vaga (string ou number)
     * @param candidateId ID do candidato (opcional)
     * @param skipValidation Se deve pular a verificação de validação (padrão: false)
     * @returns Promise com resultado da candidatura
     */
    static async applyToJobWithValidation(
        jobpostId: string | number,
        candidateId?: string,
        skipValidation: boolean = false
    ): Promise<JobApplyResult> {
        try {
            // Converter jobpostId para number se for string
            const jobpostIdNumber = typeof jobpostId === 'string' ? parseInt(jobpostId, 10) : jobpostId;
            
            // Verificar se a conversão foi bem-sucedida
            if (isNaN(jobpostIdNumber)) {
                return {
                    success: false,
                    error: 'ID da vaga inválido',
                    isValidationError: false
                };
            }

            // Verificar validação se não foi solicitado para pular
            if (!skipValidation) {
                const validation = await this.checkProfileBeforeApply(candidateId);
                
                if (!validation.isValid) {
                    return {
                        success: false,
                        error: validation.message,
                        missingFields: validation.missingFields,
                        isValidationError: true
                    };
                }
            }

            // Tentar se candidatar à vaga
            await applyToJob(jobpostIdNumber);
            
            return {
                success: true
            };
        } catch (error: any) {
            // Verificar se é erro de validação baseado na mensagem
            const errorMessage = error.message || '';
            const isValidationError = errorMessage.includes('Perfil incompleto') || 
                                    errorMessage.includes('Campos obrigatórios faltantes');
            
            return {
                success: false,
                error: errorMessage,
                isValidationError
            };
        }
    }

    /**
     * Extrai a mensagem de forma segura, lidando com arrays vazios
     * @param message Mensagem que pode ser string ou array de strings
     * @returns String da mensagem ou mensagem padrão
     */
    private static extractMessage(message: string | string[]): string {
        if (Array.isArray(message)) {
            return message.length > 0 ? message[0] : 'Erro de validação';
        }
        return message || 'Erro de validação';
    }

    /**
     * Função completa para candidatura com todas as verificações
     * @param jobpostId ID da vaga (string ou number)
     * @param candidateId ID do candidato (opcional)
     * @param skipValidation Se deve pular a verificação de validação
     * @returns Promise com resultado da candidatura
     */
    static async applyToJob(
        jobpostId: string | number,
        candidateId?: string,
        skipValidation: boolean = false
    ): Promise<JobApplyResult> {
        return await this.applyToJobWithValidation(jobpostId, candidateId, skipValidation);
    }
}

